// Public SEO page: the floor-pour concrete calculator + supporting copy.
// The calculator itself is the shared component; this page adds meta, copy
// and the funnel CTA into /tilaa.
import { lazy, Suspense } from "react";
import { useNavigate } from "react-router";
import { Box, Container, Stack, Typography } from "@mui/material";
import { usePageMeta } from "src/hooks/usePageMeta";
import { trackEvent } from "src/analytics/gtag";
import { readDraft, writeDraft } from "src/utils/quote/quoteStorage";
import { calcKestoHoursFromM3 } from "src/utils/quote/pumppuKesto";
import { captureError } from "src/utils/errorReporting";
import { useT } from "src/i18n";

// Shared lazy chunk with the wizard embed - a chunk-load failure (stale
// deploy, offline) degrades to an inline notice instead of crashing the page.
// The fallback is still rendered inside the app's LanguageProvider (mounted via
// <Suspense> further down), so it can call useT() like any other component —
// mirrors StepConcrete.jsx's ChunkLoadFallback.
function ChunkLoadFallback() {
    const t = useT();
    return (
        <Typography sx={{ color: "error.main", p: 3 }}>
            {t("laskuri.page.calculatorLoadFailed")}
        </Typography>
    );
}

const FloorPlanCalculator = lazy(() =>
    import("src/components/laskuri/FloorPlanCalculator").catch((err) => {
        captureError(err, { tags: { entity: "laskuri", operation: "chunk.load", swallowed: true } });
        return { default: ChunkLoadFallback };
    })
);

export default function Laskuri() {
    const navigate = useNavigate();
    const t = useT();
    const FAQ = [
        { q: t("laskuri.page.faq.demand.q"), a: t("laskuri.page.faq.demand.a") },
        { q: t("laskuri.page.faq.wasteAllowance.q"), a: t("laskuri.page.faq.wasteAllowance.a") },
        { q: t("laskuri.page.faq.drainSlope.q"), a: t("laskuri.page.faq.drainSlope.a") },
    ];
    usePageMeta({
        title: t("laskuri.page.pageTitle"),
        description: t("laskuri.page.pageDescription"),
        path: "/laskuri",
    });

    function handleUseInQuote({ plan, result }) {
        const existing = readDraft() || {};
        writeDraft({
            ...existing,
            maaraM3: String(result.m3Recommended),
            kesto: calcKestoHoursFromM3(result.m3Recommended),
            betoniLaatuSelvitys: { ...(existing.betoniLaatuSelvitys || {}), floorPlan: plan },
        });
        trackEvent("laskuri_to_tilaus", { m3: result.m3Recommended });
        navigate("/tilaa");
    }

    return (
        <Container maxWidth="lg" sx={{ py: { xs: 2, md: 4 } }}>
            <Stack spacing={2}>
                <Box>
                    <Typography variant="h4" component="h1" sx={{ mb: 1 }}>
                        {t("laskuri.page.heading")}
                    </Typography>
                    <Typography variant="body1" sx={{ color: "text.secondary" }}>
                        {t("laskuri.page.intro")}
                    </Typography>
                </Box>
                <Box sx={{ height: { xs: "62vh", md: "68vh" }, maxHeight: 720 }}>
                    <Suspense fallback={null}>
                        <FloorPlanCalculator onResult={handleUseInQuote} actionLabel={t("laskuri.page.useInQuoteCta")} allow3D />
                    </Suspense>
                </Box>

                <Box component="section" sx={{ pt: 3 }}>
                    <Typography variant="h5" component="h2" sx={{ mb: 1 }}>{t("laskuri.page.howComposedHeading")}</Typography>
                    <Typography variant="body2" sx={{ color: "text.secondary", mb: 1 }}>
                        {t("laskuri.page.howComposedBody1")}
                    </Typography>
                    <Typography variant="body2" sx={{ color: "text.secondary" }}>
                        {t("laskuri.page.howComposedBody2")}
                    </Typography>
                </Box>

                <Box component="section">
                    <Typography variant="h5" component="h2" sx={{ mb: 1 }}>{t("laskuri.page.faqHeading")}</Typography>
                    <Stack spacing={1.5}>
                        {FAQ.map((f) => (
                            <Box key={f.q}>
                                <Typography variant="subtitle2" component="h3">{f.q}</Typography>
                                <Typography variant="body2" sx={{ color: "text.secondary" }}>{f.a}</Typography>
                            </Box>
                        ))}
                    </Stack>
                </Box>
            </Stack>
        </Container>
    );
}
